import type { ListItem } from "../../components/List/List"
import { Employee } from "../Employee/Employee"
import { MemoryClass } from "./MemoryClass"
import { EEnableStatusType } from "../../Enum/EEnableStatusType"
import i18next from "i18next"
import { Crown, ShieldCheck, Phone, Mail, Circle } from "lucide-react"
import { getEmployeeAvatar } from "../../utils/employeeAvatar"

export class EmployeeMemory extends MemoryClass<Employee> {
  setList = (employeeList: Employee[]) => {
    this.listData = employeeList
  }

  protected cloneEntity(item: Employee): Employee {
    return new Employee({ ...item })
  }

  findByStringId = (id: string): Employee | null => {
    const item = this.listData.filter((ss) => ss.id === id)[0]

    return item ? this.cloneEntity(item) : null
  }

  isSameEmployee = (employee1: Employee | null, employee2: Employee | null): boolean => {
    if (!employee1 || !employee2) return false

    return employee1.employeeId === employee2.employeeId
  }

  getListCards = (all: boolean): ListItem[] => {
    var cards: ListItem[] = []

    let count = 0

    this.listData.forEach((ss) => {
      if ((all || ss.status === EEnableStatusType.Active) && !ss.isDeliveryEmployee) {
        cards.push(this.getCard(ss, count))
      }
    })

    count++

    return cards
  }

  getCard(ss: Employee, count: number): ListItem {
    const enable = ss.status === EEnableStatusType.Active

    const person = ss.personEntity

    return {
      isDisable: !enable,
      id: ss.employeeId,
      title: person.name,
      linkImage: ss.iconUrl,
      index: count,
      dataFilter: [person.cpf, person.rg, person.phone, person.email, person.birthDate],
      children: (
        <div
          className={`flex justify-between gap-4 p-3 transition-all duration-300${
            enable
              ? ss.isMaster
                ? "bg-gradient-to-r from-white via-amber-50 to-orange-50 hover:shadow-md hover:scale-[1.01]"
                : "bg-whitehover:shadow-md hover:scale-[1.01]"
              : "bg-gray-50 opacity-70"
          }`}
        >
          <div className="flex gap-4 flex-1">
            <div className="flex flex-col flex-1">
              <div className="flex items-center gap-2">
                {ss.isMaster && (
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500">
                    <Crown className="w-4 h-4 text-white" />
                  </div>
                )}

                <span className={`text-base font-semibold ${ss.isMaster ? "text-orange-900" : "text-gray-800"}`}>
                  {person.name}
                </span>
              </div>

              <span
                className={`mt-1 text-sm flex items-center gap-1.5 ${
                  ss.isMaster ? "text-orange-700" : "text-gray-500"
                }`}
              >
                {ss.isMaster ? (
                  <>
                    <ShieldCheck className="w-4 h-4" />
                    {i18next.t("general:label_admin")}
                  </>
                ) : (
                  `CPF: ${person.cpf || "—"}`
                )}
              </span>

              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                {ss.companyFunction !== "" && (
                  <span className="inline-flex font-bold ring-1 ring-orange-300 items-center gap-1.5 rounded-full bg-white px-2 py-1 text-gray-700">
                    {ss.companyFunction}
                  </span>
                )}

                {person.phone && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2 py-1 text-gray-600">
                    <Phone className="w-3.5 h-3.5 text-orange-500" />
                    {person.phone}
                  </span>
                )}

                {person.email && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2 py-1 text-gray-600">
                    <Mail className="w-3.5 h-3.5 text-amber-500" />

                    {person.email}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col items-end justify-between">
            {ss.isMaster && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-white px-3 py-1 text-xs font-semibold text-orange-700">
                <Crown className="w-3.5 h-3.5 text-amber-500" />
                {i18next.t("general:label_admin")}
              </span>
            )}

            {enable ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                <Circle className="w-3 h-3 fill-emerald-500 text-emerald-500" />
                Ativo
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-500">
                <Circle className="w-3 h-3 fill-gray-400 text-gray-400" />
                Inativo
              </span>
            )}
          </div>
        </div>
      ),
    }
  }

  protected getItemId(item: Employee): number {
    return item.employeeId
  }
}
